/* LJ Designs — immersive motion layer (GSAP + ScrollTrigger) */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESIGN_MODE = window.Shopify && Shopify.designMode;
  var FINE_POINTER = window.matchMedia('(pointer: fine)').matches;
  var DESKTOP = window.matchMedia('(min-width: 750px)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    /* Homepage only. Product pages and everything else keep stock Dawn
       behavior (animations.js) untouched. */
    if (window.location.pathname !== '/' && window.location.pathname !== '') return;

    /* Take over Dawn's IntersectionObserver reveals so nothing double-animates
       and nothing is left stuck at opacity 0 if this script runs. */
    document.querySelectorAll('.scroll-trigger').forEach(function (el) {
      el.classList.remove('scroll-trigger', 'scroll-trigger--offscreen');
    });

    if (REDUCED) return; /* content is now fully visible; stop here */

    var mainSections = gsap.utils.toArray('#MainContent > .shopify-section');

    /* ---------- 1. Hero entrance ---------- */
    var hero = mainSections[0];
    if (hero) {
      var heading = hero.querySelector('h1, h2');
      var caption = hero.querySelector('[class*="caption"]');
      var paragraphs = hero.querySelectorAll('p:not([class*="caption"])');
      var buttons = hero.querySelectorAll('.button');
      var media = hero.querySelector('img, video, model-viewer');

      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      if (heading && heading.textContent.trim().split(/\s+/).length <= 12) {
        /* word-split reveal (self-built; no SplitText dependency) */
        var words = heading.textContent.trim().split(/\s+/);
        heading.setAttribute('aria-label', heading.textContent.trim());
        heading.innerHTML = words
          .map(function (w) {
            return '<span class="lj-w" aria-hidden="true"><span class="lj-wi">' + w + '</span></span>';
          })
          .join(' ');
        tl.from(heading.querySelectorAll('.lj-wi'), {
          yPercent: 110,
          rotateX: -35,
          duration: 0.9,
          stagger: 0.055,
        });
      } else if (heading) {
        tl.from(heading, { opacity: 0, y: 30, duration: 0.8 });
      }

      if (caption) tl.from(caption, { opacity: 0, letterSpacing: '0.4em', duration: 0.9 }, 0.1);
      if (paragraphs.length) tl.from(paragraphs, { opacity: 0, y: 24, duration: 0.7, stagger: 0.1 }, 0.35);
      if (buttons.length) tl.from(buttons, { opacity: 0, y: 18, scale: 0.96, duration: 0.6, stagger: 0.08 }, 0.55);
      if (media) {
        gsap.set(media, { transformOrigin: '50% 50%' });
        tl.from(media, { opacity: 0, scale: 1.08, clipPath: 'inset(6% round 12px)', duration: 1.1, ease: 'power2.out' }, 0.2);
        /* gentle parallax on the hero media while it scrolls away */
        gsap.to(media, {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 },
        });
      }
      /* hero content drifts up slightly faster than scroll for depth */
      var heroContent = hero.querySelector('.banner__content, .hero__content, [class*="__text"], [class*="content"]');
      if (heroContent) {
        gsap.to(heroContent, {
          yPercent: -6,
          opacity: 0.4,
          ease: 'none',
          scrollTrigger: { trigger: hero, start: '60% top', end: 'bottom top', scrub: 0.6 },
        });
      }
    }

    /* ---------- 2. Generic section reveals (replaces Dawn's binary reveal) ---------- */
    mainSections.slice(1).forEach(function (section) {
      if (section.hasAttribute('data-lj-showcase-host')) return; /* 3D section runs its own timeline */
      var groups = section.querySelectorAll(
        'h1, h2:not(.lj-skip), .title, .image-with-text__content > *, .multicolumn-card, ' +
        '.grid__item, .accordion, [class*="comparison"] tr, .rich-text__blocks > *'
      );
      var targets = groups.length ? Array.prototype.slice.call(groups, 0, 14) : [section];
      /* de-dupe nested matches: keep outermost only */
      targets = targets.filter(function (el) {
        return !targets.some(function (other) { return other !== el && other.contains(el); });
      });
      gsap.from(targets, {
        opacity: 0,
        y: 26,
        duration: 0.6,
        stagger: 0.07,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 82%',
          toggleActions: 'play none none reverse',
        },
      });
    });

    /* ---------- 3. Image-with-text media parallax ---------- */
    gsap.utils.toArray('.image-with-text__media img').forEach(function (img) {
      var wrap = img.closest('.image-with-text__media');
      if (!wrap) return;
      wrap.style.overflow = 'hidden';
      gsap.fromTo(img, { yPercent: -7, scale: 1.12 }, {
        yPercent: 7,
        scale: 1.12,
        ease: 'none',
        scrollTrigger: { trigger: wrap, start: 'top bottom', end: 'bottom top', scrub: 0.5 },
      });
    });

    /* ---------- 4. Scroll-velocity skew on marquee/slider tracks ---------- */
    var skewTargets = [];
    document.querySelectorAll('[id*="marquee"], [id*="image_slider"], [id*="icon_text"]').forEach(function (section) {
      var nodes = section.querySelectorAll('*');
      for (var i = 0; i < nodes.length; i++) {
        if (getComputedStyle(nodes[i]).animationName !== 'none') { skewTargets.push(nodes[i]); break; }
      }
    });
    if (skewTargets.length) {
      var skewSetters = skewTargets.map(function (el) { return gsap.quickTo(el, 'skewX', { duration: 0.4, ease: 'power2.out' }); });
      var clampSkew = gsap.utils.clamp(-8, 8);
      ScrollTrigger.create({
        onUpdate: function (self) {
          var s = clampSkew(self.getVelocity() / -220);
          skewSetters.forEach(function (set) { set(s); });
        },
      });
      /* settle back to 0 */
      ScrollTrigger.addEventListener('scrollEnd', function () {
        skewSetters.forEach(function (set) { set(0); });
      });
    }

    /* ---------- 5. Magnetic primary buttons (desktop, fine pointer) ---------- */
    if (FINE_POINTER && DESKTOP) {
      document.querySelectorAll('#MainContent .button:not(.button--tertiary)').forEach(function (btn) {
        var xTo = gsap.quickTo(btn, 'x', { duration: 0.4, ease: 'elastic.out(1, 0.45)' });
        var yTo = gsap.quickTo(btn, 'y', { duration: 0.4, ease: 'elastic.out(1, 0.45)' });
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          xTo((e.clientX - r.left - r.width / 2) * 0.25);
          yTo((e.clientY - r.top - r.height / 2) * 0.35);
        });
        btn.addEventListener('mouseleave', function () { xTo(0); yTo(0); });
      });

      /* ---------- 6. Product card 3D tilt ---------- */
      document.querySelectorAll('.card-wrapper').forEach(function (card) {
        var media = card.querySelector('.card__media');
        if (!media) return;
        card.style.perspective = '900px';
        var rx = gsap.quickTo(media, 'rotationX', { duration: 0.5, ease: 'power2.out' });
        var ry = gsap.quickTo(media, 'rotationY', { duration: 0.5, ease: 'power2.out' });
        card.addEventListener('mousemove', function (e) {
          var r = card.getBoundingClientRect();
          ry(((e.clientX - r.left) / r.width - 0.5) * 8);
          rx(((e.clientY - r.top) / r.height - 0.5) * -8);
        });
        card.addEventListener('mouseleave', function () { rx(0); ry(0); });
      });
    }

    /* ---------- 7. Header: blur + shrink after scroll ---------- */
    var headerWrapper = document.querySelector('.section-header, .shopify-section-group-header-group');
    if (headerWrapper) {
      ScrollTrigger.create({
        start: 120,
        onEnter: function () { document.body.classList.add('lj-scrolled'); },
        onLeaveBack: function () { document.body.classList.remove('lj-scrolled'); },
      });
    }

    /* ---------- 8. Count-up numbers ([data-lj-count]) ---------- */
    gsap.utils.toArray('[data-lj-count]').forEach(function (el) {
      var end = parseFloat(el.getAttribute('data-lj-count'));
      var obj = { v: 0 };
      gsap.to(obj, {
        v: end,
        duration: 1.4,
        ease: 'power2.out',
        snap: { v: end % 1 === 0 ? 1 : 0.1 },
        scrollTrigger: { trigger: el, start: 'top 85%' },
        onUpdate: function () { el.textContent = end % 1 === 0 ? Math.round(obj.v) : obj.v.toFixed(1); },
      });
    });

    /* ---------- 9. 3D showcase: no pinning — model spins as the section
       scrolls through the viewport; steps reveal with a normal stagger ---------- */
    var showcase = document.querySelector('[data-lj-showcase]');
    if (showcase) {
      var mv = showcase.querySelector('model-viewer');
      var fallbackImg = showcase.querySelector('.lj-showcase__fallback img');
      var steps = showcase.querySelectorAll('.lj-showcase__step');
      var host = showcase.closest('.shopify-section');
      if (host) host.setAttribute('data-lj-showcase-host', '');

      if (steps.length) {
        gsap.from(steps, {
          opacity: 0,
          y: 24,
          duration: 0.55,
          stagger: 0.12,
          ease: 'power2.out',
          scrollTrigger: { trigger: showcase, start: 'top 75%', toggleActions: 'play none none reverse' },
        });
      }

      var spinTrigger = { trigger: showcase, start: 'top bottom', end: 'bottom top', scrub: 0.8 };
      if (mv) {
        var orbitState = { angle: -30 };
        gsap.to(orbitState, {
          angle: 210,
          ease: 'none',
          scrollTrigger: spinTrigger,
          onUpdate: function () {
            mv.cameraOrbit = orbitState.angle + 'deg 72deg 90%';
          },
        });
      } else if (fallbackImg) {
        gsap.to(fallbackImg, { rotation: 6, scale: 1.08, ease: 'none', scrollTrigger: spinTrigger });
      }
    }

    /* Watchdog: if rAF is suppressed (rare webviews / background rendering),
       kill all animation state so content is never left hidden. */
    var rafTicked = false;
    requestAnimationFrame(function () { rafTicked = true; });
    setTimeout(function () {
      if (rafTicked) return;
      document.documentElement.classList.add('lj-static');
      ScrollTrigger.getAll().forEach(function (t) { t.kill(); });
      gsap.globalTimeline.clear();
      document.querySelectorAll('#MainContent [style], .lj-wi').forEach(function (el) {
        gsap.set(el, { clearProps: 'opacity,transform,clipPath,visibility,letterSpacing' });
      });
    }, 1600);

    /* Recalculate after everything (images, fonts) settles */
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  });
})();
